import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useTranslation } from 'react-i18next';

const ProductImageUpload = ({ images, onChange }) => {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (files) => {
    const validFiles = Array.from(files).filter(file => {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not a valid image file`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum file size is 5MB`);
        return false;
      }
      return true;
    });

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: Date.now() + Math.random(),
          file,
          url: e.target.result,
          name: file.name,
          size: file.size,
          isPrimary: images.length === 0
        };
        onChange([...images, newImage]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files) {
      handleFileSelect(files);
    }
  };

  const removeImage = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    if (updatedImages.length > 0 && !updatedImages.some(img => img.isPrimary)) {
      updatedImages[0].isPrimary = true;
    }
    onChange(updatedImages);
  };

  const setPrimaryImage = (imageId) => {
    const updatedImages = images.map(img => ({
      ...img,
      isPrimary: img.id === imageId
    }));
    onChange(updatedImages);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="bg-white rounded-lg border border-border p-6">
      <div className="flex items-center mb-4">
        <Icon name="Image" size={20} className="text-primary mr-2" />
        <h3 className="text-lg font-semibold text-foreground">{t('product_image_upload_title')}</h3>
      </div>

      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragOver 
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileInputChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        
        <div className="space-y-4">
          <Icon 
            name="Upload" 
            size={48} 
            className={`mx-auto ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} 
          />
          <div>
            <p className="text-foreground font-medium">
              {dragOver ? t('drop_images_here') : t('drag_and_drop_images')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('click_to_browse')}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
          className="space-x-2 px-6 py-3 border-2 border-green-600 text-green-600 font-semibold rounded-lg hover:bg-green-600 hover:text-white transition"
            onClick={() => fileInputRef.current?.click()}
            iconName="Upload"
          >
            {t('choose_files')}
          </Button>
        </div>
      </div>

      <div className="text-xs text-muted-foreground mt-2 space-y-1">
        <p>{t('supported_formats')}</p>
        <p>{t('max_file_size')}</p>
        <p>{t('recommended_dimensions')}</p>
      </div>

      {images.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-foreground mb-3">
            {t('uploaded_images')} ({images.length})
          </h4>
          <div className="grid grid-cols-1 gap-4">
            {images.map((image) => (
              <div
                key={image.id}
                className={`relative flex items-center space-x-4 p-3 border rounded-lg ${
                  image.isPrimary ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <div className="flex-shrink-0">
                  <img
                    src={image.url}
                    alt={image.name}
                    className="w-16 h-16 object-cover rounded-md"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {image.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(image.size)}
                  </p>
                  {image.isPrimary && (
                    <div className="flex items-center mt-1">
                      <Icon name="Star" size={12} className="text-primary mr-1" />
                      <span className="text-xs text-primary font-medium">{t('primary_image')}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  {!image.isPrimary && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPrimaryImage(image.id)}
                      className="text-xs"
                    >
                      Set Primary
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeImage(image.id)}
                    className="text-destructive hover:text-destructive h-8 w-8"
                  >
                    <Icon name="Trash2" size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductImageUpload;
